import { useState } from 'react'
import { CalendarDaysIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'libs/i18n'
import { useSearchParams } from 'libs/router'
import {
	ActionsSelect,
	CardWrapper,
	ConfirmModal,
	CtaLink,
	DeleteConfirmModal,
	EmptyTable,
	FormatModal,
	Header,
	SortSelect
} from 'shared/components'
import { useSearch, useFilter, useModal, useUserRoles, useTableSelect } from 'shared/hooks'
import { SelectClickTypes } from '@table-library/react-table-library/select'
import { defaultConfig } from 'config/default'
import { routes } from 'config/routes'
import { IOption } from 'interfaces/select.interface'
import { downloadAs } from 'utils/downloadFile'
import { AxiosResponse } from 'axios'
import { useActionFilters, useContextMenuOptions, useSortingFilters } from '../../hooks'
import { useFetchBookings } from './queries/useFetchBookings'
import { useBookingsWelcomeEmailMutation } from './mutations/useBookingsWelcomeEmailMutation'
import { useBookingsEmailReminderMutation } from './mutations/useBookingsEmailReminderMutation'
import { useBookingsDeleteMutation } from './mutations/useBookingsDeleteMutation'
import { useBookingsExportMutation } from './mutations/useBookingsExportMutation'
import { useBookingsProductConfirmEmailMutation } from './mutations/useBookingsProductConfirmEmailMutation'
import { useBookingsConfirmMutation } from './mutations/useBookingsConfirmMutation'
import { useBookingsUnconfirmMutation } from './mutations/useBookingsUnonfirmMutation'
import { FiltersSection } from './organisms/FiltersSection/FiltersSection'
import { TableSection } from './organisms/TableSection'

const BookingsPage = () => {
	const { t } = useTranslation()
	const [searchParams] = useSearchParams()
	const { handleSearch } = useSearch({ name: 'search' })
	const { handleFilter } = useFilter()
	const [checkedBookings, setCheckedBookings] = useState<number[]>([])
	const [chosenBooking, setChosenBooking] = useState<number | null>(null)
	const [chosenExportFormat, setChosenExportFormat] = useState<number | string | null>(null)
	const { isStaff, isUser } = useUserRoles()
	const { bookings, isBookingsLoading } = useFetchBookings()
	const { select } = useTableSelect({ nodes: bookings?.data?.results ?? [] }, onSelectChange, {
		clickType: SelectClickTypes.ButtonClick
	})
	const clearSelection = () => select.fns.onRemoveAll()
	const sortingFilters = useSortingFilters()
	const [actionsFilter] = useActionFilters(!checkedBookings.length)

	const {
		isOpen: isBulkProductConfirmOpen,
		openModal: openBulkProductConfirmModal,
		closeModal: closeBulkProductConfirmModal
	} = useModal()
	const {
		isOpen: isProductConfirmOpen,
		openModal: openProductConfirmModal,
		closeModal: closeProductConfirmModal
	} = useModal()
	const {
		isOpen: isBulkWelcomeEmailOpen,
		openModal: openBulkWelcomeEmailModal,
		closeModal: closeBulkWelcomeEmailModal
	} = useModal()
	const {
		isOpen: isWelcomeEmailOpen,
		openModal: openWelcomeEmailModal,
		closeModal: closeWelcomeEmailModal
	} = useModal()
	const {
		isOpen: isBulkEmailReminderOpen,
		openModal: openBulkEmailReminderModal,
		closeModal: closeBulkEmailReminderModal
	} = useModal()
	const {
		isOpen: isEmailReminderOpen,
		openModal: openEmailReminderModal,
		closeModal: closeEmailReminderModal
	} = useModal()
	const {
		isOpen: isExportModalOpen,
		openModal: openExportModal,
		closeModal: closeExportModal
	} = useModal()
	const {
		isOpen: isFormatModalOpen,
		openModal: openFormatModal,
		closeModal: closeFormatModal
	} = useModal()
	const {
		isOpen: isMultipleDeleteModalOpen,
		openModal: openMultipleDeleteModal,
		closeModal: closeMultipleDeleteModal
	} = useModal()
	const {
		isOpen: isBulkConfirmOpen,
		openModal: openBulkConfirmModal,
		closeModal: closeBulkConfirmModal
	} = useModal()
	const {
		isOpen: isConfirmOpen,
		openModal: openConfirmModal,
		closeModal: closeConfirmModal
	} = useModal()
	const {
		isOpen: isBulkUnconfirmOpen,
		openModal: openBulkUnconfirmModal,
		closeModal: closeBulkUnconfirmModal
	} = useModal()
	const {
		isOpen: isUnconfirmOpen,
		openModal: openUnconfirmModal,
		closeModal: closeUnconfirmModal
	} = useModal()

	const handleAction = (action: string) => {
		switch (action) {
			case 'smsReminder':
			case 'emailConfirm':
				openBulkProductConfirmModal()
				break
			case 'emailWelcome':
				openBulkWelcomeEmailModal()
				break
			case 'emailReminder':
				openBulkEmailReminderModal()
				break
			case 'export':
				openExportModal()
				break
			case 'delete':
				openMultipleDeleteModal()
				break
			case 'confirm':
				openBulkConfirmModal()
				break
			case 'unconfirm':
				openBulkUnconfirmModal()
				break
		}
	}

	const { bookingsProductConfirmEmailMutation } = useBookingsProductConfirmEmailMutation(() => {
		closeBulkProductConfirmModal()
		closeProductConfirmModal()
	})

	const { bookingsWelcomeEmailMutation } = useBookingsWelcomeEmailMutation(() => {
		closeBulkWelcomeEmailModal()
		closeWelcomeEmailModal()
	})

	const { bookingsConfirmMutation } = useBookingsConfirmMutation(() => {
		closeBulkConfirmModal()
		closeConfirmModal()
	})

	const { bookingsUnconfirmMutation } = useBookingsUnconfirmMutation(() => {
		closeBulkUnconfirmModal()
		closeUnconfirmModal()
	})

	const { bookingsEmailReminderMutation } = useBookingsEmailReminderMutation(() => {
		closeBulkEmailReminderModal()
		closeEmailReminderModal()
	})

	const { exportBookingsMutation, isExportBookingsLoading } = useBookingsExportMutation({
		onSuccess: (res: AxiosResponse<ArrayBuffer>) => {
			const format = defaultConfig.exportFormatOptions
				.find((format) => format.value === chosenExportFormat)
				?.label.toLowerCase()
			if (format) {
				downloadAs({ file: res.data, name: 'bookings-export', format })
			}
			clearSelection()
		},
		onSettled: () => {
			closeFormatModal()
		}
	})

	const { bookingsDeleteMutation } = useBookingsDeleteMutation(() => {
		clearSelection()
		closeMultipleDeleteModal()
	})

	const handleExportModal = () => {
		closeExportModal()
		openFormatModal()
	}

	const handleFormatChange = (data: IOption) => {
		data?.value?.toString() ? setChosenExportFormat(data.value) : setChosenExportFormat(null)
	}

	function onSelectChange(action: any, state: any) {
		setCheckedBookings(state.ids)
	}

	const handleSmsReminder = (id: number) => {
		setChosenBooking(id)
		openProductConfirmModal()
	}

	const handleEmailWelcome = (id: number) => {
		setChosenBooking(id)
		openWelcomeEmailModal()
	}

	const handleEmailReminder = (id: number) => {
		setChosenBooking(id)
		openEmailReminderModal()
	}

	const handleConfirm = (ids: number[]) => {
		setChosenBooking(ids[0])
		openConfirmModal()
	}

	const handleUnconfirm = (ids: number[]) => {
		setChosenBooking(ids[0])
		openUnconfirmModal()
	}

	const getMenuOptions = useContextMenuOptions({
		handleSmsReminder,
		handleEmailWelcome,
		handleEmailReminder,
		handleConfirm,
		handleUnconfirm
	})

	const renderContent = () => {
		if (isBookingsLoading) return <EmptyTable isLoading />
		if (!bookings?.data?.results?.length)
			return (
				<EmptyTable text={searchParams.toString() ? t('No results') : t('No active bookings')} />
			)

		return (
			<TableSection
				bookings={bookings}
				isLoading={isBookingsLoading}
				select={select}
				getMenuOptions={getMenuOptions}
			/>
		)
	}

	return (
		<>
			<Header
				title={t('Bookings')}
				actions={
					<>
						{!isUser && (
							<CtaLink
								to={routes.bookingCalendar}
								text={t('Calendar')}
								icon={<CalendarDaysIcon />}
								variant="outline"
								compact
							/>
						)}
						<SortSelect {...sortingFilters} onChange={handleFilter} />
						{isStaff && <CtaLink to={routes.bookingAdd} text={t('Add booking')} compact />}
					</>
				}
			/>
			<div className="mx-auto max-w-[1736px] px-5 lg:flex lg:max-h-[calc(100vh-72px-4rem)] lg:flex-col lg:px-0">
				<CardWrapper className="lg:flex lg:min-h-0 lg:flex-1 lg:flex-col">
					<FiltersSection
						actions={
							<ActionsSelect
								variant="filters"
								options={actionsFilter.options}
								onChange={handleAction}
							/>
						}
						handleSearch={handleSearch}
						handleFilter={handleFilter}
					/>
					{renderContent()}
				</CardWrapper>
			</div>

			{isBulkProductConfirmOpen && (
				<ConfirmModal
					title={t('Request product confirm email')}
					isOpened={isBulkProductConfirmOpen}
					closeModal={closeBulkProductConfirmModal}
					confirm={() => bookingsProductConfirmEmailMutation(checkedBookings)}>
					<p>
						{t(
							'You are about to request product confirm for multiple bookings. Are you sure you want to proceed?'
						)}
					</p>
				</ConfirmModal>
			)}
			{isProductConfirmOpen && (
				<ConfirmModal
					title={t('Request product confirm email')}
					isOpened={isProductConfirmOpen}
					closeModal={closeProductConfirmModal}
					confirm={() => bookingsProductConfirmEmailMutation([chosenBooking as number])}>
					<p>
						{t(
							'You are about to request product confirm email for a booking. Are you sure you want to proceed?'
						)}
					</p>
				</ConfirmModal>
			)}
			{isBulkWelcomeEmailOpen && (
				<ConfirmModal
					title={t('Request welcome email')}
					isOpened={isBulkWelcomeEmailOpen}
					closeModal={closeBulkWelcomeEmailModal}
					confirm={() => bookingsWelcomeEmailMutation(checkedBookings)}>
					<p>
						{t(
							'You are about to request welcome email for multiple bookings. Are you sure you want to proceed?'
						)}
					</p>
				</ConfirmModal>
			)}
			{isBulkConfirmOpen && (
				<ConfirmModal
					title={t('Confirm')}
					isOpened={isBulkConfirmOpen}
					closeModal={closeBulkConfirmModal}
					confirm={() => bookingsConfirmMutation(checkedBookings)}>
					<p>
						{t(
							'You are about to request confirmation for multiple bookings. Are you sure you want to proceed?'
						)}
					</p>
				</ConfirmModal>
			)}
			{isConfirmOpen && (
				<ConfirmModal
					title={t('Confirm')}
					isOpened={isConfirmOpen}
					closeModal={closeConfirmModal}
					confirm={() => bookingsConfirmMutation([chosenBooking])}>
					<p>
						{t(
							'You are about to request confirmation for booking. Are you sure you want to proceed?'
						)}
					</p>
				</ConfirmModal>
			)}
			{isBulkUnconfirmOpen && (
				<ConfirmModal
					title={t('Unonfirm')}
					isOpened={isBulkUnconfirmOpen}
					closeModal={closeBulkUnconfirmModal}
					confirm={() => bookingsUnconfirmMutation(checkedBookings)}>
					<p>
						{t(
							'You are about to request unconfirmation for multiple bookings. Are you sure you want to proceed?'
						)}
					</p>
				</ConfirmModal>
			)}
			{isUnconfirmOpen && (
				<ConfirmModal
					title={t('Unonfirm')}
					isOpened={isUnconfirmOpen}
					closeModal={closeUnconfirmModal}
					confirm={() => bookingsUnconfirmMutation([chosenBooking])}>
					<p>
						{t(
							'You are about to request unconfirmation for booking. Are you sure you want to proceed?'
						)}
					</p>
				</ConfirmModal>
			)}
			{isWelcomeEmailOpen && (
				<ConfirmModal
					title={t('Request welcome email')}
					isOpened={isWelcomeEmailOpen}
					closeModal={closeWelcomeEmailModal}
					confirm={() => bookingsWelcomeEmailMutation([chosenBooking])}>
					<p>
						{t(
							'You are about to request welcome email for a booking. Are you sure you want to proceed?'
						)}
					</p>
				</ConfirmModal>
			)}
			{isBulkEmailReminderOpen && (
				<ConfirmModal
					title={t('Request email reminder')}
					isOpened={isBulkEmailReminderOpen}
					closeModal={closeBulkEmailReminderModal}
					confirm={() => bookingsEmailReminderMutation(checkedBookings)}>
					<p>
						{t(
							'You are about to request email reminder for multiple bookings. Are you sure you want to proceed?'
						)}
					</p>
				</ConfirmModal>
			)}
			{isEmailReminderOpen && (
				<ConfirmModal
					title={t('Request email reminder')}
					isOpened={isEmailReminderOpen}
					closeModal={closeEmailReminderModal}
					confirm={() => bookingsEmailReminderMutation([chosenBooking])}>
					<p>
						{t(
							'You are about to request email reminder for a booking. Are you sure you want to proceed?'
						)}
					</p>
				</ConfirmModal>
			)}
			{isExportModalOpen && (
				<ConfirmModal
					title={t('Request export')}
					isOpened={isExportModalOpen}
					closeModal={closeExportModal}
					confirm={handleExportModal}>
					<p>
						{t(
							'You are about to request export for multiple bookings. Are you sure you want to proceed?'
						)}
					</p>
				</ConfirmModal>
			)}
			{isFormatModalOpen && (
				<FormatModal
					disableButton={chosenExportFormat === null}
					isOpened={isFormatModalOpen}
					closeModal={closeFormatModal}
					confirm={() =>
						exportBookingsMutation({ ids: checkedBookings, format: chosenExportFormat })
					}
					isLoading={isExportBookingsLoading}
					onChange={handleFormatChange}
				/>
			)}
			{isMultipleDeleteModalOpen && (
				<DeleteConfirmModal
					title={t('Delete bookings')}
					isOpened={isMultipleDeleteModalOpen}
					closeModal={closeMultipleDeleteModal}
					confirm={() => bookingsDeleteMutation(checkedBookings)}>
					<p>
						{t('You are about delete {{count}} bookings. Are you sure you want to proceed?', {
							count: checkedBookings.length
						})}
					</p>
				</DeleteConfirmModal>
			)}
		</>
	)
}

export default BookingsPage
